import { Injectable, effect, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, debounceTime, shareReplay, switchMap, tap, map } from 'rxjs';
import { CmlConfig } from 'src/app/cml-toolbox/interfaces/cml-config';
import { HttpClient } from '@angular/common/http';
import { CmlConfigs } from '@orm-entities/cml-configs';
import { NetworkStatusService } from '../../core/services/network-status.service';
import { UrlService } from '../../core/services/url.service';
import { DatabaseService } from '../../core/services/database.service';

const FETCH_DEBOUNCE_MS = 60_000;

@Injectable({
  providedIn: 'root',
})
export class CMLConfigurationService {
  #networkStatusService = inject(NetworkStatusService);
  #httpClient = inject(HttpClient);
  #urlService = inject(UrlService);
  #databaseService = inject(DatabaseService);

  #refresh$ = new BehaviorSubject<void>(undefined);
  #fetchTrigger$ = new Subject<void>();
  public configurations$: Observable<CmlConfig[]> = this.#refresh$.pipe(
    switchMap(() => this.#databaseService.select(CmlConfigs).pipe(map(entities => entities.map(this.convertToModel)))),
    shareReplay(1),
  );

  public constructor() {
    this.#fetchTrigger$
      .pipe(
        debounceTime(FETCH_DEBOUNCE_MS),
        switchMap(() => this.fetchAndPersistLocally()),
      )
      .subscribe();

    effect(() => {
      if (this.#networkStatusService.isConnectivityStable()) {
        this.#fetchTrigger$.next();
      }
    });
  }

  public fetchAndPersistLocally(): Observable<CmlConfig[]> {
    return this.#httpClient.get<CmlConfig[]>(this.#urlService.getUrl('settings/cml-tool')).pipe(
      switchMap(configs => this.#databaseService.upsertAndPrune(CmlConfigs, configs.map(this.convertToEntity)).pipe(map(() => configs))),
      tap(() => {
        this.#refresh$.next();
      }),
    );
  }

  private convertToModel(entity: CmlConfigs): CmlConfig {
    return {
      id: entity.id,
      startId: entity.startId,
      step: entity.step,
      tenantId: entity.tenantId,
      customPointValues: entity.customPointValues ? (entity.customPointValues as unknown as string).split(',') : [],
    };
  }

  private convertToEntity(config: CmlConfig): CmlConfigs {
    const entity = new CmlConfigs();
    entity.id = config.id;
    entity.startId = config.startId;
    entity.step = config.step;
    entity.tenantId = config.tenantId;
    (entity.customPointValues as unknown as string) = config.customPointValues.join(',');
    return entity;
  }
}
